import { createReducer, on } from '@ngrx/store';

import {
  ICancelTicketResponseData,
  ICMProducto,
  ICPMotivo,
  ICreateTicketResponseData,
  ITicketActivoResponseData,
  IUpdateTicketResponseData,
} from '../../shared/models/CambiosMercado.interface';
import {
  cancelTicket,
  clearCancelTicket,
  clearCreateTicket,
  clearGetCPCatalogo,
  clearGetCPReasons,
  clearGetTicketActivo,
  clearUpdateTicket,
  createTicket,
  errorCancelTicket,
  errorCreateTicket,
  errorGetCPCatalogo,
  errorGetCPReasons,
  errorGetTicketActivo,
  errorUpdateTicket,
  getCPCatalogo,
  getCPReasons,
  getTicketActivo,
  successCancelTicket,
  successCreateTicket,
  successGetCPCatalogo,
  successGetCPReasons,
  successGetTicketActivo,
  successUpdateTicket,
  updateTicket,
} from '../actions/cambios-mercado.actions';

interface IGeneric {
    error: any;
    failed: boolean;
    success: boolean;
    loading: boolean;
}

/**
 * * Estado para el catálogo de productos
 */
export interface CatalogoCPState extends IGeneric {
    data: ICMProducto[];
}

export const CatalogoCPInit: CatalogoCPState = {
    error: null,
    failed: false,
    success: false,
    loading: false,
    data: [],
};

const _catalogoCPReducer = createReducer(
    CatalogoCPInit,
    on(getCPCatalogo, (state) => ({
        ...state,
        loading: true,
        success: false,
        failed: false,
    })),
    on(successGetCPCatalogo, (state, data) => ({
        ...state,
        loading: false,
        success: true,
        data: data.productos.map(p => ({ ...p, filtro: `${p.clave_producto} ${p.descripcion}` })),
    })),
    on(errorGetCPCatalogo, (state, { payload }) => ({
        ...state,
        loading: false,
        failed: true,
        error: payload,
    })),
    on(clearGetCPCatalogo, (state) => ({ ...state, ...CatalogoCPInit })),
);

export function CatalogoCPReducer(state: any, action: any) {
    return _catalogoCPReducer(state, action);
}

/**
 * * Consultar catalogo de motivos por SKU
 */
export interface CatalogoReasonsState extends IGeneric {
    data: ICPMotivo[];
}

export const CatalogoReasonsInit: CatalogoReasonsState = {
    error: null,
    failed: false,
    success: false,
    loading: false,
    data: [],
};

const _catalogoReasonsReducer = createReducer(
    CatalogoReasonsInit,
    on(getCPReasons, (state) => ({
        ...state,
        loading: true,
        success: false,
        failed: false,
        data: [],
    })),
    on(successGetCPReasons, (state, data) => ({
        ...state,
        loading: false,
        success: true,
        data: data.motivos,
    })),
    on(errorGetCPReasons, (state, { payload }) => ({
        ...state,
        loading: false,
        failed: true,
        error: payload,
    })),
    on(clearGetCPReasons, (state) => ({ ...state, ...CatalogoReasonsInit })),
);

export function CatalogoReasonsReducer(state: any, action: any) {
    return _catalogoReasonsReducer(state, action);
}

/**
 * * Estado para crear ticket
 */
export interface CreateTicketState extends IGeneric {
    data: ICreateTicketResponseData;
}

export const CreateTicketInit: CreateTicketState = {
    error: null,
    failed: false,
    success: false,
    loading: false,
    data: {} as ICreateTicketResponseData,
};

const _createTicketReducer = createReducer(
    CreateTicketInit,
    on(createTicket, (state) => ({
        ...state,
        loading: true,
        success: false,
        failed: false,
    })),
    on(successCreateTicket, (state, data) => ({
        ...state,
        loading: false,
        success: true,
        data,
    })),
    on(errorCreateTicket, (state, { payload }) => ({
        ...state,
        loading: false,
        failed: true,
        error: payload,
    })),
    on(clearCreateTicket, (state) => ({ ...state, ...CreateTicketInit })),
);

export function CreateTicketReducer(state: any, action: any) {
    return _createTicketReducer(state, action);
}

/**
 * * Estado para cancelar ticket
 */
export interface CancelTicketState extends IGeneric {
    data: ICancelTicketResponseData;
}

export const CancelTicketInit: CancelTicketState = {
    error: null,
    failed: false,
    success: false,
    loading: false,
    data: {} as ICancelTicketResponseData,
};

const _cancelTicketReducer = createReducer(
    CancelTicketInit,
    on(cancelTicket, (state) => ({
        ...state,
        loading: true,
        success: false,
        failed: false,
    })),
    on(successCancelTicket, (state, data) => ({
        ...state,
        loading: false,
        success: true,
        data,
    })),
    on(errorCancelTicket, (state, { payload }) => ({
        ...state,
        loading: false,
        failed: true,
        error: payload,
    })),
    on(clearCancelTicket, (state) => ({ ...state, ...CancelTicketInit })),
);

export function CancelTicketReducer(state: any, action: any) {
    return _cancelTicketReducer(state, action);
}

/**
 * * Estado para actualizar ticket
 */
export interface UpdateTicketState extends IGeneric {
    data: IUpdateTicketResponseData;
}

export const UpdateTicketInit: UpdateTicketState = {
    error: null,
    failed: false,
    success: false,
    loading: false,
    data: {} as IUpdateTicketResponseData,
};

const _updateTicketReducer = createReducer(
    UpdateTicketInit,
    on(updateTicket, (state) => ({
        ...state,
        loading: true,
        success: false,
        failed: false,
    })),
    on(successUpdateTicket, (state, data) => ({
        ...state,
        loading: false,
        success: true,
        data,
    })),
    on(errorUpdateTicket, (state, { payload }) => ({
        ...state,
        loading: false,
        failed: true,
        error: payload,
    })),
    on(clearUpdateTicket, (state) => ({ ...state, ...UpdateTicketInit })),
);

export function UpdateTicketReducer(state: any, action: any) {
    return _updateTicketReducer(state, action);
}

/**
 * * Estado para consultar ticket activo
 */
export interface GetTicketActivoState extends IGeneric {
    data: ITicketActivoResponseData | {};
}

export const GetTicketActivoInit: GetTicketActivoState = {
    error: null,
    failed: false,
    success: false,
    loading: false,
    data: {} as ITicketActivoResponseData,
};

const _getTicketActivoReducer = createReducer(
    GetTicketActivoInit,
    on(getTicketActivo, (state) => ({
        ...state,
        loading: true,
        success: false,
        failed: false,
    })),
    on(successGetTicketActivo, (state, { data }) => ({
        ...state,
        loading: false,
        success: true,
        data,
    })),
    on(errorGetTicketActivo, (state, { payload }) => ({
        ...state,
        loading: false,
        failed: true,
        error: payload,
    })),
    on(clearGetTicketActivo, (state) => ({ ...state, ...GetTicketActivoInit })),
);

export function GetTicketActivoReducer(state: any, action: any) {
    return _getTicketActivoReducer(state, action);
}
